import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAvailability, useSlots } from "../../application/useScheduler";
import {
    formatBookingTime,
    nextDateWithAvailability,
    toDateKey,
    weekdayOfDateKey,
} from "../../domain/formatting";
import { ScheduledSession, SchedulerSettings } from "../../domain/types";
import { sessionTypeOf, subjectOf } from "../../domain/subject";
import { Modal } from "../../../../shared/ui/modal";
import SlotGrid from "./SlotGrid";
import { openDatePicker } from "./openDatePicker";

interface RescheduleSessionModalProps {
    /** The session being moved. `null` closes the dialog. */
    session: ScheduledSession | null;
    onClose: () => void;
    onSubmit: (start: string) => void;
    settings: SchedulerSettings;
    /** The subject's display name, for the line naming what is being moved. */
    subjectLabel?: string | null;
    isSaving?: boolean;
    errorMessage?: string | null;
}

/**
 * Moving a session to another time.
 *
 * Deliberately narrower than `ScheduleSessionModal`: the API accepts only a new
 * start, so who the session is with, whether it is online and where it is held
 * all stay as they were. Offering those fields here would show controls that
 * change nothing.
 *
 * The session's current time is stated at the top, because the grid cannot show
 * it — the slot it occupies is not offered while it holds it.
 */
export default function RescheduleSessionModal({
    session,
    onClose,
    onSubmit,
    settings,
    subjectLabel = null,
    isSaving = false,
    errorMessage = null,
}: RescheduleSessionModalProps) {
    const { t } = useTranslation();
    const open = session !== null;
    const currentDay = session ? session.date.slice(0, 10) : "";

    const [date, setDate] = useState(currentDay);
    const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

    // The booking moves into a slot as long as a session of its own kind.
    const sessionType = sessionTypeOf(session ? subjectOf(session)?.kind : null);
    const slotsQuery = useSlots(open ? date : "", sessionType);
    const availabilityQuery = useAvailability();

    const windows = useMemo(
        () => availabilityQuery.data ?? [],
        [availabilityQuery.data]
    );

    const activeWeekdays = useMemo(
        () =>
            new Set(
                windows
                    .filter((window) => window.is_active)
                    .map((window) => window.day_of_week)
            ),
        [windows]
    );

    const dayHasAvailability =
        activeWeekdays.size === 0
            ? false
            : activeWeekdays.has(weekdayOfDateKey(date));

    /**
     * Open on the day the session is already on, so moving it by an hour costs
     * no navigation. A session whose day has passed, or that sits on a weekday
     * no longer worked, seeds forward instead — an empty grid on opening reads
     * as a fault rather than as "nothing free that day".
     *
     * Seeded once per session, as in `ScheduleSessionModal`: `windows` is a new
     * array on every refetch, and a refetch fires when the native date picker
     * returns focus, which would otherwise overwrite the day just chosen.
     */
    const seededFor = useRef<string | null>(null);

    useEffect(() => {
        if (!session) {
            seededFor.current = null;
            return;
        }
        if (seededFor.current === session.id) return;
        const suggested = nextDateWithAvailability(
            new Date(),
            windows,
            settings.timezone
        );
        seededFor.current = session.id;
        setDate(
            activeWeekdays.has(weekdayOfDateKey(currentDay)) &&
                currentDay >= suggested
                ? currentDay
                : suggested
        );
        setSelectedSlot(null);
    }, [session, currentDay, windows, activeWeekdays, settings.timezone]);

    // A slot chosen for one date must not survive a date change.
    useEffect(() => {
        setSelectedSlot(null);
    }, [date]);

    return (
        <Modal
            isOpen={open}
            onClose={onClose}
            showCloseButton={false}
            ariaLabel={t("scheduler.schedule.rescheduleTitle")}
            className="max-w-lg overflow-hidden rounded-3xl"
        >
            {/* Fields at 16px on a phone: any smaller and iOS zooms the page
                on focus, leaving the therapist to pinch back out. */}
            <div className="flex max-h-[90vh] w-full flex-col gap-4 overflow-y-auto p-5 max-sm:[&_input]:text-base max-sm:[&_select]:text-base sm:p-6">
                <h3 className="text-lg font-semibold">
                    {t("scheduler.schedule.rescheduleTitle")}
                </h3>

                {session && (
                    <div className="flex flex-col gap-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-gray-700">
                        <span className="text-content-tertiary">
                            {t("scheduler.schedule.currentTime")}
                        </span>
                        <p className="font-medium">
                            {formatBookingTime(
                                currentDay,
                                session.hour,
                                settings.timezone
                            )}
                            {subjectLabel ? ` · ${subjectLabel}` : ""}
                        </p>
                    </div>
                )}

                <label className="flex flex-col gap-1 text-sm">
                    {t("scheduler.schedule.date")}
                    <input
                        type="date"
                        aria-label={t("scheduler.schedule.date")}
                        min={toDateKey(new Date(), settings.timezone)}
                        value={date}
                        onChange={(event) => setDate(event.target.value)}
                        onClick={openDatePicker}
                        onFocus={openDatePicker}
                        className="w-full cursor-pointer rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    />
                </label>

                <div className="flex flex-col gap-1 text-sm">
                    <span>{t("scheduler.schedule.newTime")}</span>
                    <SlotGrid
                        slots={slotsQuery.data?.slots ?? []}
                        timezone={slotsQuery.data?.timezone ?? settings.timezone}
                        selected={selectedSlot}
                        onSelect={setSelectedSlot}
                        isLoading={
                            slotsQuery.isLoading || availabilityQuery.isLoading
                        }
                        isError={slotsQuery.isError}
                        onRetry={() => slotsQuery.refetch()}
                        disabled={isSaving}
                        dayHasAvailability={dayHasAvailability}
                        hasAnyAvailability={activeWeekdays.size > 0}
                    />
                </div>

                {/* Said before confirming, not after: the message goes out on
                    save and cannot be recalled. */}
                <p className="text-xs text-content-tertiary">
                    {t("scheduler.schedule.rescheduleNotice")}
                </p>

                {errorMessage && (
                    <p role="alert" className="text-sm text-red-600">
                        {errorMessage}
                    </p>
                )}

                {/* On a phone the form is longer than the screen, and the
                    dialog has no close button: the way out and the way
                    forward stay pinned to the bottom, within a thumb's reach. */}
                <div className="sticky -bottom-5 -mx-5 -mb-5 flex gap-2 border-t border-gray-100 bg-white px-5 py-3 dark:border-white/10 dark:bg-gray-900 sm:static sm:m-0 sm:justify-end sm:border-0 sm:bg-transparent sm:p-0 sm:dark:bg-transparent">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 rounded-lg border border-gray-200 px-4 py-2.5 text-sm dark:border-white/10 sm:flex-none sm:border-0 sm:py-2"
                    >
                        {t("scheduler.common.cancel")}
                    </button>
                    <button
                        type="button"
                        disabled={!selectedSlot || isSaving}
                        onClick={() => onSubmit(selectedSlot as string)}
                        className="flex-1 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50 sm:flex-none sm:py-2"
                    >
                        {isSaving
                            ? t("scheduler.schedule.rescheduling")
                            : t("scheduler.schedule.rescheduleConfirm")}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
